import { throwError } from "@/lib/throwError";
import { get, post } from "@/services/axiosUitils";
import {
  Event,
  EventListResponse,
  EventDetailsResponse,
} from "@/utils/types/event";

const basePath = "/city_collab/events";

class EventService {
  /**
   * Get all events
   */
  getAllEvents = async (
    notifyMessage?: boolean,
    filters?: { organization_id?: string; event?: string }
  ): Promise<Event[]> => {
    try {
      const params: Record<string, string> = {};
      if (filters?.organization_id) {
        params.organization_id = filters.organization_id;
      }
      if (filters?.event) {
        params.event = filters.event;
      }

      const response = await get<EventListResponse>(`${basePath}/`, {
        params,
        notifyMessage,
      });

      if (response?.success && response?.data) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch events", "EventService");
      return [];
    }
  };

  /**
   * Get event by ID
   */
  getEventById = async (
    id: string,
    notifyMessage?: boolean
  ): Promise<Event | null> => {
    if (!id) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    try {
      const response = await get<EventListResponse>(`${basePath}/`, {
        notifyMessage,
      });

      if (response?.success && response?.data) {
        const event = response.data.find(e => e.id === id);
        return event ?? null;
      }

      return null;
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch event", "EventService");
      return null;
    }
  };

  /**
   * Get event details by ID from details endpoint
   */
  getEventDetails = async (
    eventId: string,
    notifyMessage?: boolean
  ): Promise<Event | null> => {
    if (!eventId) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    try {
      const response = await get<EventDetailsResponse>(`${basePath}/details/`, {
        params: {
          event_id: eventId,
        },
        notifyMessage,
      });

      if (response?.success && response?.data) {
        return response.data;
      }

      return null;
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch event details", "EventService");
      return null;
    }
  };

  /**
   * Express interest in an event
   */
  expressEventInterest = async (
    eventId: string,
    interested: boolean,
    notifyMessage?: boolean
  ): Promise<any> => {
    if (!eventId) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    try {
      const response = await post(
        "/city_collab/event-interest/",
        {
          event_id: eventId,
          interested: interested,
        },
        {
          notifyMessage,
        }
      );

      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to express interest in event", "EventService");
      throw error;
    }
  };

  /**
   * Buy tickets for an event
   */
  buyTickets = async (
    payload: {
      eventid: string;
      total_amount: string;
      fullname: string;
      email: string;
      mobile: string;
      tickets: Array<{
        ticket_id: string;
        no_of_ticket: number;
        price: string;
        amount: string;
      }>;
    },
    notifyMessage?: boolean
  ): Promise<any> => {
    if (!payload.eventid) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    if (!payload.tickets || payload.tickets.length === 0) {
      throwError("At least one ticket is required", "EventService");
      return null;
    }

    try {
      const response = await post("/city_collab/buy-tickets/", payload, {
        notifyMessage,
      });

      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to buy tickets", "EventService");
      throw error;
    }
  };

  /**
   * Get event tickets by event ID
   */
  getEventTickets = async (
    eventId: string,
    notifyMessage?: boolean
  ): Promise<any[]> => {
    if (!eventId) {
      throwError("Event ID is required", "EventService");
      return [];
    }

    try {
      const response = await get<any>("/city_collab/event-tickets/", {
        params: {
          event_id: eventId,
        },
        notifyMessage,
      });

      // Handle different response structures
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }

      return [];
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch event tickets", "EventService");
      return [];
    }
  };

  /**
   * Submit a donation for an event
   */
  submitDonation = async (
    payload: {
      event_id: string;
      name: string;
      email: string;
      phone: string;
      donation_amount: string;
      message?: string;
      donation_id?: string;
    },
    notifyMessage?: boolean
  ): Promise<any> => {
    if (!payload.event_id) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    if (
      !payload.name ||
      !payload.email ||
      !payload.phone ||
      !payload.donation_amount
    ) {
      throwError(
        "Name, email, phone, and donation amount are required",
        "EventService"
      );
      return null;
    }

    try {
      const response = await post("/city_collab/donations/", payload, {
        notifyMessage,
      });

      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to submit donation", "EventService");
      throw error;
    }
  };

  /**
   * Get donations for an event
   */
  getEventDonations = async (
    eventId: string,
    notifyMessage?: boolean
  ): Promise<any[]> => {
    if (!eventId) {
      throwError("Event ID is required", "EventService");
      return [];
    }

    try {
      const response = await get<any>("/city_collab/donations/list/", {
        params: {
          event_id: eventId,
        },
        notifyMessage,
      });

      // Handle paginated response structure with results array
      // The get function returns response.data, so response already contains the API response
      if (response?.results && Array.isArray(response.results)) {
        return response.results;
      } else if (
        response?.data?.results &&
        Array.isArray(response.data.results)
      ) {
        return response.data.results;
      } else if (response?.data && Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }

      return [];
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch donations", "EventService");
      return [];
    }
  };

  /**
   * Get sponsor payment / registration rows for an event (admin grid)
   */
  getEventSponsorsList = async (
    eventId: string,
    notifyMessage?: boolean
  ): Promise<any[]> => {
    if (!eventId) {
      throwError("Event ID is required", "EventService");
      return [];
    }

    try {
      const response = await get<any>("/city_collab/sponsors/list/", {
        params: {
          event_id: eventId,
        },
        notifyMessage,
      });

      if (response?.results && Array.isArray(response.results)) {
        return response.results;
      }
      if (response?.data?.results && Array.isArray(response.data.results)) {
        return response.data.results;
      }
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }
      if (Array.isArray(response)) {
        return response;
      }

      return [];
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch sponsors list", "EventService");
      return [];
    }
  };

  /**
   * Get volunteer activity rows for current user
   */
  getVolunteerSignups = async (
    _eventId?: string,
    notifyMessage?: boolean
  ): Promise<any[]> => {
    try {
      const response = await get<any>(
        "/city_collab/volunteer-signup/volunteer-events/",
        {
          notifyMessage,
        }
      );

      if (response?.success && Array.isArray(response.data)) {
        return response.data;
      }

      const legacyResponse = await get<any>("/city_collab/volunteer-signups/", {
        notifyMessage,
      });

      if (legacyResponse?.data && Array.isArray(legacyResponse.data)) {
        return legacyResponse.data;
      } else if (
        legacyResponse?.results &&
        Array.isArray(legacyResponse.results)
      ) {
        return legacyResponse.results;
      } else if (Array.isArray(legacyResponse)) {
        return legacyResponse;
      }

      return [];
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch volunteer signups", "EventService");
      return [];
    }
  };
}

const instance = new EventService();
export default instance;
